import { OfficeMember } from '@/components/OfficeMember';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useSubmitPayeeInfo } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useSubmitPayeeInfo';
import {
  EmailReceivingMethodData,
  EmailReceivingMethodForm,
  InvoiceReceiveEmailProcessType,
  SECTION_KEYS,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { checkIsServerError } from '@/features/errors/utils';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Form,
  FormRadioButton,
  FormSelectProps,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import parentStyles from '../../../../styles.module.scss';
import { HeaderSection, SingleRow, SingleRowWithCopy } from '../../../Common';
import localStyles from './styles.module.scss';
import { useEmailAddressRowConfig } from './useEmailAddressRowConfig';

const cx = classnames.bind({ ...parentStyles, ...localStyles });

export type Props = {
  data?: EmailReceivingMethodData;
  apPayeeId: string;
};

export const Edit: FC<Props> = memo(({ data, apPayeeId }) => {
  const { setIsEditingSection, isEditingSection } = usePayeeDetailDrawer();
  const { t } = useTranslation();

  const defaultOptions = useMemo(() => {
    if (
      !data?.contact_office_member?.id ||
      !data?.contact_office_member?.display_name ||
      !data?.contact_office_member?.number
    )
      return [];
    return [
      {
        value: data.contact_office_member.id,
        label: data.contact_office_member.display_name,
        caption: data.contact_office_member.number,
      },
    ];
  }, [data?.contact_office_member]);

  const defaultValues: EmailReceivingMethodForm = useMemo(
    () => ({
      contact_office_member_id: data?.contact_office_member?.id ?? undefined,
      ap_invoice_receive_email_setting: {
        process_invoice_type:
          data?.invoice_receive_email_process_type ===
          InvoiceReceiveEmailProcessType.PAYMENT_REQUEST
            ? InvoiceReceiveEmailProcessType.PAYMENT_REQUEST
            : InvoiceReceiveEmailProcessType.RECEIVED_INVOICE,
      },
    }),
    [data]
  );

  const methods = useForm<EmailReceivingMethodForm>({
    defaultValues,
    mode: 'all',
  });

  const { isValid } = useFormState({ control: methods.control });
  const { onSubmit, isLoading } = useSubmitPayeeInfo<EmailReceivingMethodForm>({
    apPayeeId,
  });

  const processInvoiceType = useWatch({
    control: methods.control,
    name: 'ap_invoice_receive_email_setting.process_invoice_type',
  });

  const isPaymentRequest =
    processInvoiceType === InvoiceReceiveEmailProcessType.PAYMENT_REQUEST;

  const handleCancel = () => {
    setIsEditingSection(SECTION_KEYS.NULL);
  };

  const processInvoiceTypeOptions = useMemo(
    () => [
      {
        id: 'payment_request',
        label: t('ap_payee_mail_receive_payment_request_draft'),
        value: InvoiceReceiveEmailProcessType.PAYMENT_REQUEST,
        disabled: false,
      },
      {
        id: 'received_invoice',
        label: t('received_invoice'),
        value: InvoiceReceiveEmailProcessType.RECEIVED_INVOICE,
        disabled: false,
      },
    ],
    [t]
  );

  const selectProps: Omit<
    FormSelectProps<EmailReceivingMethodForm>['selectProps'],
    'options'
  > = useMemo(() => {
    return {
      mode: 'single',
      placeholder: t('ap_payee_contact_office_member_names_placeholder'),
    };
  }, [t]);

  const officeMemberRules = useMemo(() => ({ required: false }), []);

  const emailAddressRowConfig = useEmailAddressRowConfig({ data });

  return (
    <Form<EmailReceivingMethodForm>
      formMethod={methods}
      onSubmit={onSubmit}
      testId='email-receiving-method-edit'
      className={cx('subSectionContainer')}
    >
      <HeaderSection
        displayName={t('ap_payee_mail_receive')}
        isSubHeader
        actionButton={
          <ButtonGroup direction='horizontal'>
            <ButtonV2
              color='primary'
              isSecondary
              onClick={handleCancel}
              isDisabled={isLoading}
              testId='cancel-button'
              containerClassName={cx('cancelButton')}
            >
              {t('cancel')}
            </ButtonV2>
            <ButtonV2
              color='primary'
              size='xs'
              type='submit'
              isLoading={isLoading}
              isTransparent={isLoading}
              isDisabled={
                !isValid || isLoading || isEditingSection !== SECTION_KEYS.EMAIL
              }
              testId='save-button'
              containerClassName={cx('saveButton')}
            >
              {t('save')}
            </ButtonV2>
          </ButtonGroup>
        }
      />
      <div className={cx('bodySectionContainer')}>
        <SingleRow
          label={t('ap_payee_detail_drawer_email_receiving_method_location')}
          renderContent={
            <FormRadioButton<EmailReceivingMethodForm>
              options={processInvoiceTypeOptions}
              control={methods.control}
              name='ap_invoice_receive_email_setting.process_invoice_type'
              inputId='process_invoice_type'
            />
          }
        />

        {isPaymentRequest && (
          <SingleRow
            label={t('ap_payee_mail_receive_contact_office_member_name')}
            helperLabel={t(
              'ap_payee_detail_drawer_email_receiving_method_contact_office_member_name_tooltip'
            )}
            renderContent={
              <SuspenseErrorBoundary
                shouldPropagate={checkIsServerError}
                fallback={<SuspenseLoading />}
              >
                <OfficeMember<EmailReceivingMethodForm>
                  control={methods.control}
                  name='contact_office_member_id'
                  inputId='contact-office-member-id'
                  selectProps={selectProps}
                  defaultOptions={defaultOptions}
                  rules={officeMemberRules}
                />
              </SuspenseErrorBoundary>
            }
          />
        )}

        {emailAddressRowConfig &&
          (emailAddressRowConfig.shouldShowCopyButton ? (
            <SingleRowWithCopy
              label={t('ap_payee_detail_drawer_email_receiving_method_address')}
              value={data?.invoice_receive_email_address ?? ''}
            />
          ) : (
            <SingleRow
              label={t('ap_payee_detail_drawer_email_receiving_method_address')}
              value={data?.invoice_receive_email_address ?? ''}
              contentClassName={
                emailAddressRowConfig.isSecondaryText
                  ? cx('secondaryText')
                  : undefined
              }
            />
          ))}
      </div>
    </Form>
  );
});

Edit.displayName = 'EmailReceivingMethodEdit';
